import { useEffect, useState } from "react";
import { Bug, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, NativeSelect } from "@/components/common";
import { NewBugDialog, BugDetailDialog, STATUS_LABEL, SEV_STYLE } from "@/components/BugDialogs";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

export default function Bugs() {
  const [data, setData] = useState({ items: [], counts: {} });
  const [filter, setFilter] = useState({ status: "", severity: "" });
  const [adding, setAdding] = useState(false);
  const [openId, setOpenId] = useState(null);
  const load = () => api.get("/bugs", { params: Object.fromEntries(Object.entries(filter).filter(([, v]) => v)) }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div data-testid="bugs-page">
      <PageHeader eyebrow="QA" title="Bug tracker" subtitle="Log failed test cases with screenshots and track every fix to Closed.">
        <Button className="rounded-full bg-[#0F382C] hover:bg-[#134E3A]" onClick={() => setAdding(true)} data-testid="new-bug-btn"><Plus className="mr-1 h-4 w-4" />Log bug</Button>
      </PageHeader>
      <div className="mb-4 grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid="bug-status-board">
        {Object.entries(STATUS_LABEL).map(([k, l]) => (
          <button key={k} onClick={() => setFilter({ ...filter, status: filter.status === k ? "" : k })} className={`rounded-2xl border p-3 text-left transition-colors ${filter.status === k ? "border-[#0F382C] bg-[#0F382C] text-white" : "border-[#E7E2D6] bg-white"}`} data-testid={`bug-count-${k}`}>
            <div className="text-xs opacity-80">{l}</div><div className="font-mono text-2xl font-semibold">{data.counts[k] || 0}</div>
          </button>
        ))}
      </div>
      <div className="mb-3 flex gap-2">
        <NativeSelect value={filter.severity} onChange={(e) => setFilter({ ...filter, severity: e.target.value })} className="w-44" data-testid="bug-filter-severity">
          <option value="">All severities</option>{["critical", "high", "medium", "low"].map((s) => <option key={s} value={s}>{s}</option>)}
        </NativeSelect>
      </div>
      <div className="rounded-2xl border border-[#E7E2D6] bg-white p-2 sm:p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Bug</TableHead><TableHead>Test case</TableHead><TableHead>Severity</TableHead><TableHead>Status</TableHead><TableHead>Reported</TableHead></TableRow></TableHeader>
          <TableBody>
            {data.items.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground"><Bug className="mx-auto mb-2 h-6 w-6" />No bugs here</TableCell></TableRow>}
            {data.items.map((b) => (
              <TableRow key={b.id} className="cursor-pointer" onClick={() => setOpenId(b.id)} data-testid={`bug-row-${b.id}`}>
                <TableCell><div className="font-semibold">{b.code} · {b.title}</div><div className="text-xs text-muted-foreground">{b.module}{b.screenshots.length ? ` · ${b.screenshots.length} screenshot(s)` : ""}</div></TableCell>
                <TableCell className="font-mono text-xs">{b.test_case_id || "-"}</TableCell>
                <TableCell><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${SEV_STYLE[b.severity]}`}>{b.severity}</span></TableCell>
                <TableCell className="text-sm">{STATUS_LABEL[b.status]}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{b.reporter_name}<div>{fmtDateTime(b.created_at)}</div></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <NewBugDialog open={adding} onOpenChange={setAdding} onCreated={load} />
      <BugDetailDialog bugId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
}
